/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */
import CancelSubscriptionModal from './modal/cancel_subscription';
import CardWrapper from '../wrapper/card';
import { PlanInvoiceObject } from '../../params/redux/subscription';
import SelectedPlanCheckoutComponent from './selected_plan_checkout';
import TimeRemaining from '../reusable/time_remaining';
import { AppDispatch, RootState } from '../../redux/store';
import { Box, Button, Typography } from '@mui/material';
import { brand, green, red } from '../../theme/themePrimitives';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { fetchCurrentPlanDetailsThunk, specialUpgradePlanThunk } from '../../redux/thunks/subscription';
import { formatDate, formatDateWithTime } from '../../helper';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';

interface SubscriptionsRow {
    id: number;
    dateTime: string;
    plan: string;
    amount: string;
    status: string;
    url: string;
}

const SubscriptionComponent = () => {
    const dispatch = useDispatch<AppDispatch>();
    // const navigate = useNavigate();
    const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
    const { invoices, subscription, selectedPlan } = useSelector((state: RootState) => state.subscription);
    const { currentUser } = useSelector((state: RootState) => state.app);

    const listOfInvoices: SubscriptionsRow[] = invoices.map((invoice: PlanInvoiceObject) => ({
        id: invoice?.id,
        dateTime: invoice?.created_at,
        plan: `${invoice?.plan?.product_name} | ${invoice?.plan?.product_type}`,
        amount: `$${(parseInt(invoice.amount_paid, 10)).toFixed(2)
            .toString()}`,
        status: invoice?.status,
        url: invoice?.download_url
    })) || [];

    useEffect(() => {
        const fetchSubscriptionDetail = async () => {
            try {
                await dispatch(fetchCurrentPlanDetailsThunk()).unwrap();
            } catch (e) {
                //
            }
        };
        fetchSubscriptionDetail();
    }, []);

    const specialUpgradePlanHandler = async () => {
        try {
            const response: {
                url?: string
            } = await dispatch(specialUpgradePlanThunk()).unwrap() as {
                url?: string;
            };
            if (response && response.url) {
                window.location.href = response?.url;
            }
        } catch (e) {
            //
        }
    };

    const columns: GridColDef<SubscriptionsRow>[] = [
        {
            field: 'dateTime',
            headerName: 'DATE & TIME',
            flex: 1,
            renderCell: (params: GridRenderCellParams<SubscriptionsRow, string>) => <Box display="flex" gap="10px" alignItems="center" my={1}>
                <Typography variant="subtitle2" color="textSecondary">{params.value ? formatDateWithTime(params.value) : ''}</Typography>
            </Box>
        },
        {
            field: 'plan',
            headerName: 'PLAN',
            flex: 1,
            renderCell: (params: GridRenderCellParams<SubscriptionsRow, string>) => <Box my={1}>
                <Box bgcolor={brand[50]} borderRadius="5px" width="max-content" color={brand[300]} padding="4px 8px">
                    <Typography variant="body1" fontWeight="bold">{params.row.plan}</Typography>
                </Box>
            </Box>
        },
        { field: 'amount', headerName: 'AMOUNT', flex: 1 },
        {
            field: 'status',
            headerName: 'STATUS',
            flex: 1,
            renderCell: (params: GridRenderCellParams<SubscriptionsRow, string>) => <Box my={1}>
                <Box bgcolor={green[100]} borderRadius="5px" width="max-content" color={green[400]} padding="4px 8px">
                    <Typography variant="body1" fontWeight="bold">{params.value}</Typography>
                </Box>
            </Box>
        },
        {
            field: 'id',
            headerName: 'INVOICE',
            flex: 1,
            renderCell: (params: GridRenderCellParams<SubscriptionsRow, number>) => <Box display="flex" alignItems="center" gap="2px" my={1}>
                <Button variant="outlined" size="small">
                    <a href={params.row.url} rel="noreferrer" target="_blank" style={{ textDecoration: 'none' }}>
                        Download
                    </a>
                </Button>
            </Box>
        }
    ];

    if (selectedPlan) {
        return <SelectedPlanCheckoutComponent />;
    }

    return <Box py={2} display="flex" flexDirection="column" gap={2}>
        {subscription && subscription.discount_msg && <CardWrapper>
            <Typography variant="h5" mb={2} fontWeight="bold">
                {subscription && subscription.discount_msg} {subscription.discount_percent === 50 && <TimeRemaining time={subscription.remaining_days === 0 ? 1 : subscription.remaining_days}unit="days" onTimeExpired={() => console.log('')} />}
            </Typography>
            <Button variant="contained"
                sx={{ display: currentUser?.special_link && currentUser.subscription_status === 'trialing' ? 'block' : 'none' }}
                disabled={currentUser?.role !== 'organizationOwner'} onClick={specialUpgradePlanHandler}>Upgrade Plan</Button>
        </CardWrapper>}
        <CardWrapper>
            <Typography variant="h5" fontWeight="bold">Plan Details</Typography>
            <Box display="flex">
                <Box display="flex" flexDirection="column" gap={2} minWidth="500px">
                    <Box>
                        <Typography variant="subtitle1" >Your Current Plan: {subscription && subscription.plan && subscription?.plan?.product_name?.toLocaleUpperCase()}</Typography>
                        <Typography variant="subtitle2" color="textDisabled">{subscription && subscription.plan && subscription.plan?.product_description}</Typography>
                    </Box>
                    <Box display="flex" gap={2}>
                        {/* <Box display={currentUser?.special_link && currentUser.subscription_status === 'trialing' ? 'block' : 'none'} flexDirection="column" gap={1}>
                            <Typography variant="body2" color="red">
                                Special OCA Discount: 50% OFF
                            </Typography>
                            <Button variant="contained"
                                sx={{ display: currentUser?.special_link && currentUser.subscription_status === 'trialing' ? 'block' : 'none' }}
                                disabled={currentUser?.role !== 'organizationOwner'} onClick={specialUpgradePlanHandler}>Upgrade Plan</Button>
                        </Box> */}
                        <Button sx={{ paddingY: '7px' }} variant="outlined" color="error" disabled={currentUser?.role !== 'organizationOwner'} onClick={() => setShowCancelModal(true)}>Cancel Subscription</Button>
                    </Box>
                </Box>
                <Box display="flex" flexDirection="column" gap={1}>
                    <Box>
                        <Typography variant="subtitle1">{currentUser?.subscription_status === 'trialing' ? 'Trial ends' : 'Renews'} on {subscription && subscription?.end_date
                            ? formatDate(subscription?.end_date, {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                            })
                            : ''}</Typography>
                        <Typography variant="subtitle2" color="textDisabled">Your plan auto-renews for uninterrupted access to Gonzy.</Typography>
                    </Box>
                    {currentUser?.subscription_status === 'trialing' || currentUser?.subscription_status === 'past_due'
                        ? <Box
                            display="flex"
                            justifyContent="start"
                            alignItems="center"
                            mt={1}
                        >
                            <Box
                                bgcolor={red[300]}
                                color="white"
                                width="max-content"
                                fontSize="16px"
                                px={2}
                                py="2px"
                                borderRadius={1}
                            >
                                {currentUser?.subscription_status === 'past_due' ? 'Past Due' : 'Trial'}
                            </Box>
                        </Box>
                        : <Box>
                            <Typography variant="subtitle1">Last Billing</Typography>
                            <Box display="flex" gap={2} alignItems="center">
                                <Typography variant="subtitle2" color="textDisabled">{subscription && subscription?.created_at
                                    ? formatDate(subscription?.created_at, {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                    })
                                    : ''} - $ {subscription && subscription.plan && subscription?.plan.product_price}</Typography>
                                <Box component="span" bgcolor={brand[50]} borderRadius="5px" color={brand[300]} padding="4px 8px">
                                    <Typography variant="body1" fontWeight="bold">{subscription && subscription.plan && subscription?.plan?.product_name?.toLocaleUpperCase()} | {subscription && subscription.plan && subscription?.plan.product_type}</Typography>
                                </Box>
                            </Box>
                        </Box>}
                </Box>
            </Box>
        </CardWrapper>

        <CardWrapper>
            <Typography variant="h5" fontWeight="bold" mb={2}>Invoices</Typography>
            <Box height="50vh" width="100%">
                <DataGrid
                    rows={listOfInvoices as SubscriptionsRow[] || []}
                    // loading={loaders.isLoading}
                    columns={columns}
                    pagination
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 100
                            }
                        }
                    }}
                    sx={{
                        '& .MuiDataGrid-row--borderBottom': {
                            backgroundColor: `${brand[50]} !important`
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 'bold'
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            fontWeight: 'bold'
                        },
                        '& .MuiDataGrid-cell': {
                            fontSize: '14px'
                        },
                        'width': '100%',
                        'overflowX': 'hidden'
                    }}
                />
            </Box>
        </CardWrapper>
        <CancelSubscriptionModal open={showCancelModal} onClose={() => setShowCancelModal(false)} />
    </Box>;
};

export default SubscriptionComponent;